import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Assignment as ProjectIcon,
  AccountBalance as BillingIcon,
  Security as SecurityIcon,
  Check as CheckIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNotify } from '../../ui/NotificationSystem';
import { clientService } from '../../../services/ClientService';

interface ClientFormData {
  // Company Information
  companyName: string;
  industry: string;
  companySize: string;
  website: string;
  description: string;
  
  // Primary Contact
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  primaryContactTitle: string;
  
  // Address Information
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  
  // Additional Contacts
  additionalContacts: Contact[];
  
  // Project Requirements
  projectType: string;
  budget: string;
  timeline: string;
  projectDescription: string;
  technologies: string[];
  
  // Billing Information
  billingContactName: string;
  billingContactEmail: string;
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  taxId: string;
  paymentTerms: string;
  
  // Portal Access
  portalAccess: boolean;
  accessLevel: string;
  notifications: {
    projectUpdates: boolean;
    billing: boolean;
    marketing: boolean;
  };
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
  department: string;
}

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Manufacturing',
  'Real Estate',
  'Non-Profit',
  'Government',
  'Entertainment',
  'Other'
];

const COMPANY_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '500+ employees'
];

const PROJECT_TYPES = [
  'Web Development',
  'Mobile App Development',
  'E-commerce Platform',
  'Custom Software',
  'UI/UX Design',
  'Digital Marketing',
  'Consulting',
  'Maintenance & Support',
  'Other'
];

const TECHNOLOGIES = [
  'React',
  'Vue.js',
  'Angular',
  'Node.js',
  'Python',
  'PHP',
  'Laravel',
  'WordPress',
  'React Native',
  'Flutter',
  'iOS',
  'Android',
  'AWS',
  'Google Cloud',
  'Azure'
];

const BUDGET_RANGES = [
  'Under $10,000',
  '$10,000 - $25,000',
  '$25,000 - $50,000',
  '$50,000 - $100,000',
  '$100,000 - $250,000',
  '$250,000+'
];

const TIMELINES = [
  '1-3 months',
  '3-6 months',
  '6-12 months',
  '1+ years'
];

const steps = [
  'Company Information',
  'Contact Details',
  'Project Requirements',
  'Billing & Portal Setup'
];

const AddClientPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<ClientFormData>({
    companyName: '',
    industry: '',
    companySize: '',
    website: '',
    description: '',
    
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    primaryContactTitle: '',
    
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    
    additionalContacts: [],
    
    projectType: '',
    budget: '',
    timeline: '',
    projectDescription: '',
    technologies: [],
    
    billingContactName: '',
    billingContactEmail: '',
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    },
    taxId: '',
    paymentTerms: 'Net 30',
    
    portalAccess: true,
    accessLevel: 'standard',
    notifications: {
      projectUpdates: true,
      billing: true,
      marketing: false
    }
  });

  const [contactDialog, setContactDialog] = useState(false);
  const [newContact, setNewContact] = useState<Omit<Contact, 'id'>>({
    name: '',
    email: '',
    phone: '',
    title: '',
    department: ''
  });

  const { success: notifySuccess, error: notifyError } = useNotify();

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    try {
      await clientService.createClient({
        name: formData.primaryContactName,
        email: formData.primaryContactEmail,
        phone: formData.primaryContactPhone,
        company: formData.companyName,
        website: formData.website,
        industry: formData.industry,
        status: 'active'
      });
      
      notifySuccess('Client created successfully!');
      navigate('/dashboard/admin/clients');
    } catch (error) {
      console.error('Failed to create client:', error);
      notifyError('Failed to create client');
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!(formData.companyName && formData.industry && formData.primaryContactName && formData.primaryContactEmail);
      case 1:
        return !!(formData.address.street && formData.address.city && formData.address.state);
      case 2:
        return !!(formData.projectType && formData.budget && formData.timeline);
      case 3:
        return !!(formData.billingContactName && formData.billingContactEmail);
      default:
        return true;
    }
  };

  const updateFormData = (path: string, value: any) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addContact = () => {
    if (newContact.name && newContact.email) {
      const contact: Contact = {
        ...newContact,
        id: Date.now().toString()
      };
      setFormData(prev => ({
        ...prev,
        additionalContacts: [...prev.additionalContacts, contact]
      }));
      setNewContact({
        name: '',
        email: '',
        phone: '',
        title: '',
        department: ''
      });
      setContactDialog(false);
    }
  };

  const removeContact = (id: string) => {
    setFormData(prev => ({
      ...prev,
      additionalContacts: prev.additionalContacts.filter(c => c.id !== id)
    }));
  };

  const copyAddressToBilling = () => {
    setFormData(prev => ({
      ...prev,
      billingAddress: { ...prev.address }
    }));
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon />
              Company Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.companyName}
                  onChange={(e) => updateFormData('companyName', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    value={formData.industry}
                    onChange={(e) => updateFormData('industry', e.target.value)}
                    label="Industry"
                  >
                    {INDUSTRIES.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Company Size</InputLabel>
                  <Select
                    value={formData.companySize}
                    onChange={(e) => updateFormData('companySize', e.target.value)}
                    label="Company Size"
                  >
                    {COMPANY_SIZES.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={(e) => updateFormData('website', e.target.value)}
                  placeholder="https://www.example.com"
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Company Description"
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  placeholder="Brief description of the company and its business..."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              Primary Contact
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.primaryContactName}
                  onChange={(e) => updateFormData('primaryContactName', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Job Title"
                  value={formData.primaryContactTitle}
                  onChange={(e) => updateFormData('primaryContactTitle', e.target.value)}
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.primaryContactEmail}
                  onChange={(e) => updateFormData('primaryContactEmail', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.primaryContactPhone}
                  onChange={(e) => updateFormData('primaryContactPhone', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Address & Additional Contacts
            </Typography>
            
            <Typography variant="subtitle2" gutterBottom>
              Company Address
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address.street}
                  onChange={(e) => updateFormData('address.street', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) => updateFormData('address.city', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 3 }}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.address.state}
                  onChange={(e) => updateFormData('address.state', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 3 }}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.address.zipCode}
                  onChange={(e) => updateFormData('address.zipCode', e.target.value)}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.address.country}
                  onChange={(e) => updateFormData('address.country', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">
                Additional Contacts
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setContactDialog(true)}
              >
                Add Contact
              </Button>
            </Box>
            
            {formData.additionalContacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No additional contacts added yet
              </Typography>
            ) : (
              <List>
                {formData.additionalContacts.map((contact) => (
                  <ListItem key={contact.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                    <ListItemText
                      primary={`${contact.name} - ${contact.title}`}
                      secondary={`${contact.email} • ${contact.phone} • ${contact.department}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeContact(contact.id)}
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ProjectIcon />
              Project Requirements
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Project Type</InputLabel>
                  <Select
                    value={formData.projectType}
                    onChange={(e) => updateFormData('projectType', e.target.value)}
                    label="Project Type"
                  >
                    {PROJECT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Budget Range</InputLabel>
                  <Select
                    value={formData.budget}
                    onChange={(e) => updateFormData('budget', e.target.value)}
                    label="Budget Range"
                  >
                    {BUDGET_RANGES.map((range) => (
                      <MenuItem key={range} value={range}>
                        {range}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Timeline</InputLabel>
                  <Select
                    value={formData.timeline}
                    onChange={(e) => updateFormData('timeline', e.target.value)}
                    label="Timeline"
                  >
                    {TIMELINES.map((timeline) => (
                      <MenuItem key={timeline} value={timeline}>
                        {timeline}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <Autocomplete
                  multiple
                  options={TECHNOLOGIES}
                  value={formData.technologies}
                  onChange={(_, newValue) => updateFormData('technologies', newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Preferred Technologies"
                      placeholder="Select technologies"
                    />
                  )}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Project Description"
                  value={formData.projectDescription}
                  onChange={(e) => updateFormData('projectDescription', e.target.value)}
                  placeholder="Detailed description of the project requirements..."
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BillingIcon />
              Billing Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Billing Contact Name"
                  value={formData.billingContactName}
                  onChange={(e) => updateFormData('billingContactName', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Billing Contact Email"
                  type="email"
                  value={formData.billingContactEmail}
                  onChange={(e) => updateFormData('billingContactEmail', e.target.value)}
                  required
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">
                    Billing Address
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={copyAddressToBilling}
                  >
                    Copy from Company Address
                  </Button>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.billingAddress.street}
                  onChange={(e) => updateFormData('billingAddress.street', e.target.value)}
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.billingAddress.city}
                  onChange={(e) => updateFormData('billingAddress.city', e.target.value)}
                />
              </Grid>
              
              <Grid size={{ xs: 3 }}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.billingAddress.state}
                  onChange={(e) => updateFormData('billingAddress.state', e.target.value)}
                />
              </Grid>
              
              <Grid size={{ xs: 3 }}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.billingAddress.zipCode}
                  onChange={(e) => updateFormData('billingAddress.zipCode', e.target.value)}
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Tax ID"
                  value={formData.taxId}
                  onChange={(e) => updateFormData('taxId', e.target.value)}
                />
              </Grid>
              
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Payment Terms</InputLabel>
                  <Select
                    value={formData.paymentTerms}
                    onChange={(e) => updateFormData('paymentTerms', e.target.value)}
                    label="Payment Terms"
                  >
                    <MenuItem value="Net 15">Net 15</MenuItem>
                    <MenuItem value="Net 30">Net 30</MenuItem>
                    <MenuItem value="Net 45">Net 45</MenuItem>
                    <MenuItem value="Net 60">Net 60</MenuItem>
                    <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon />
              Portal Access
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.portalAccess}
                    onChange={(e) => updateFormData('portalAccess', e.target.checked)}
                  />
                }
                label="Enable Client Portal Access"
              />
            </Box>
            
            {formData.portalAccess && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select
                      value={formData.accessLevel}
                      onChange={(e) => updateFormData('accessLevel', e.target.value)}
                      label="Access Level"
                    >
                      <MenuItem value="basic">Basic - View only</MenuItem>
                      <MenuItem value="standard">Standard - View and comment</MenuItem>
                      <MenuItem value="advanced">Advanced - Full project access</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notification Preferences
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifications.projectUpdates}
                        onChange={(e) => updateFormData('notifications.projectUpdates', e.target.checked)}
                      />
                    }
                    label="Project Updates"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifications.billing}
                        onChange={(e) => updateFormData('notifications.billing', e.target.checked)}
                      />
                    }
                    label="Billing Notifications"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notifications.marketing}
                        onChange={(e) => updateFormData('notifications.marketing', e.target.checked)}
                      />
                    }
                    label="Marketing Updates"
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard/admin/clients')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">
          Add New Client
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>
                  {label}
                </StepLabel>
                <StepContent>
                  {renderStepContent(index)}
                  
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        variant="outlined"
                      >
                        Back
                      </Button>
                      
                      {index === steps.length - 1 ? (
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          startIcon={<SaveIcon />}
                          disabled={!validateStep(index)}
                        >
                          Create Client
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          disabled={!validateStep(index)}
                        >
                          Continue
                        </Button>
                      )}
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="h6">Client Created Successfully!</Typography>
                <Typography>
                  The client has been added to your system and portal access has been configured.
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={() => {
                  setActiveStep(0);
                  setFormData({} as ClientFormData);
                }}
              >
                Add Another Client
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Contact Dialog */}
      <Dialog open={contactDialog} onClose={() => setContactDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Additional Contact</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={newContact.name}
                onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Job Title"
                value={newContact.title}
                onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newContact.phone}
                onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Department"
                value={newContact.department}
                onChange={(e) => setNewContact(prev => ({ ...prev, department: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialog(false)}>Cancel</Button>
          <Button 
            onClick={addContact} 
            variant="contained"
            disabled={!newContact.name || !newContact.email}
          >
            Add Contact
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddClientPage;
